import { handleError } from '@anchorage/sentry';
import type { UploadedFile } from '@latitude/file-upload';

const userErrorMessage =
  'Something went wrong on our end while uploading this file. Please try again.';

const useFileUpload = () => {
  const uploadFile = (
    fileWithProgress: UploadedFile,
    uploadURL: string,
    uploadFileName: string,
    onComplete?: (
      file: UploadedFile,
      response: XMLHttpRequest['response'],
      error?: Error,
    ) => void,
  ): UploadedFile | null => {
    const fileWithUpdatedProgress: UploadedFile = { ...fileWithProgress };

    if (!uploadURL || !fileWithUpdatedProgress.file) {
      const errorMessage = `Invalid ${!uploadURL ? 'upload URL' : 'file'}`;
      const error = new Error(errorMessage);
      handleError(new Error(errorMessage, { cause: error }));
      if (onComplete) {
        fileWithUpdatedProgress.errorMessage = userErrorMessage;
        onComplete(fileWithUpdatedProgress, undefined, error);
      }
      return null;
    }

    const xhr = new XMLHttpRequest();
    xhr.open('POST', uploadURL, true);

    // Track upload progress
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        fileWithUpdatedProgress.progress = percentComplete;
        if (onComplete) {
          onComplete(fileWithUpdatedProgress, xhr.response);
        }
      }
    };

    // Handle successful completion
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        fileWithUpdatedProgress.progress = 100;
        if (onComplete) {
          onComplete(fileWithUpdatedProgress, xhr.response);
        }
      } else {
        const error = new Error(`Upload failed with status: ${xhr.status}`);
        handleError(error);
        fileWithUpdatedProgress.errorMessage = userErrorMessage;
        if (onComplete) {
          onComplete(fileWithUpdatedProgress, xhr.response, error);
        }
      }
    };

    // Handle network errors
    xhr.onerror = () => {
      const error = new Error('Network error occurred during upload');
      handleError(error);
      fileWithUpdatedProgress.errorMessage = userErrorMessage;
      if (onComplete) {
        onComplete(fileWithUpdatedProgress, xhr.response, error);
      }
    };

    // Handle aborted uploads
    xhr.onabort = () => {
      const error = new Error('Upload was aborted');
      handleError(error);
      fileWithUpdatedProgress.errorMessage = userErrorMessage;
      if (onComplete) {
        onComplete(fileWithUpdatedProgress, xhr.response, error);
      }
    };

    const formData = new FormData();
    formData.append(uploadFileName ?? '', fileWithUpdatedProgress.file);
    xhr.send(formData);

    return fileWithUpdatedProgress;
  };

  return { uploadFile };
};

export { useFileUpload };
